import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useToast } from '../components/Toast';
import { captureError } from '../lib/sentry';

/**
 * Authentication ჰუკი - Supabase Auth
 * 
 * @param {Object} options - ოფციები
 * @param {boolean} options.rememberMe - დამახსოვრება (1 თვე)
 * @returns {Object} auth ინფორმაცია
 */
export function useAuth(options = {}) {
  const toast = useToast();
  const { rememberMe = true } = options;

  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // სესიის ხანგრძლივობა
  const sessionDuration = rememberMe ? 30 * 24 * 60 * 60 * 1000 : 24 * 60 * 60 * 1000; // 1 თვე ან 1 დღე

  // მომხმარებლის პროფილის მიღება
  const fetchProfile = useCallback(async (userId) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Profile fetch error:', err);
      return null;
    }
  }, []);

  // საწყისი დასეტაპება
  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        // მივიღოთ მიმდინარე სესია
        const { data: { session }, error } = await supabase.auth.getSession();

        if (error) throw error;

        if (mounted) {
          setUser(session?.user ?? null);
          
          if (session?.user) {
            const profileData = await fetchProfile(session.user.id);
            setProfile(profileData);
          }
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
        if (mounted) {
          setError(err);
          captureError(err, { tags: { feature: 'auth', action: 'initialize' } });
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    initializeAuth();

    // Auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event, session?.user?.id);

        if (event === 'SIGNED_IN' && session) {
          setUser(session.user);
          const profileData = await fetchProfile(session.user.id);
          setProfile(profileData);
          toast.success('კეთილი იყოს თქვენი მობრძანება!');
        }

        if (event === 'SIGNED_OUT') {
          setUser(null);
          setProfile(null);
          toast.info('გამოხვედით სისტემიდან');
        }

        if (event === 'TOKEN_REFRESHED') {
          setUser(session?.user ?? null);
        }

        if (event === 'USER_UPDATED') {
          if (session?.user) {
            const profileData = await fetchProfile(session.user.id);
            setProfile(profileData);
          }
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [fetchProfile, toast]);

  // შესვლა (Email + Password)
  const signIn = useCallback(async (email, password, remember = true) => {
    setIsLoading(true);
    setError(null);

    try {
      // დასეტაპება session duration
      if (!remember) {
        await supabase.auth.setSession({
          expires_at: Math.floor(Date.now() / 1000) + (24 * 60 * 60), // 24 საათი
        });
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.toLowerCase().trim(),
        password,
      });

      if (error) throw error;

      // მივიღოთ პროფილი
      const profileData = await fetchProfile(data.user.id);
      setProfile(profileData);

      toast.success('წარმატებით შესული ხართ!');
      return { user: data.user, profile: profileData };
    } catch (err) {
      console.error('Sign in error:', err);
      setError(err);
      captureError(err, { 
        tags: { feature: 'auth', action: 'signin' },
        extra: { email }
      });

      const message = err.message?.includes('Invalid login credentials')
        ? 'არასწორი ელ-ფოსტა ან პაროლი'
        : 'შესვლისას მოხდა შეცდომა';

      toast.error(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [fetchProfile, toast]);

  // გამოსვლა
  const signOut = useCallback(async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      setUser(null);
      setProfile(null);
      toast.success('გამოხვედით სისტემიდან');
    } catch (err) {
      console.error('Sign out error:', err);
      captureError(err, { tags: { feature: 'auth', action: 'signout' } });
      toast.error('გამოსვლისას მოხდა შეცდომა');
      throw err;
    }
  }, [toast]);

  // პაროლის შეცვლა
  const changePassword = useCallback(async (currentPassword, newPassword) => {
    try {
      // ჯერ გადავამოწმოთ მიმდინარე პაროლი
      const { data: { user: currentUser }, error: signInError } = await supabase.auth.signInWithPassword({
        email: user?.email,
        password: currentPassword,
      });

      if (signInError) throw signInError;

      // შევცვალოთ პაროლი
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) throw updateError;

      // ლოგირება audit-ში
      await supabase.from('audit_logs').insert([{
        table_name: 'auth.users',
        record_id: user.id,
        action: 'UPDATE',
        event_type: 'PASSWORD_CHANGE',
        new_data: { changed_at: new Date().toISOString() },
        changed_by: user.id
      }]);

      toast.success('პაროლი წარმატებით შეიცვალა!');
      return true;
    } catch (err) {
      console.error('Password change error:', err);
      captureError(err, { tags: { feature: 'auth', action: 'password_change' } });
      
      const message = err.message?.includes('Invalid login credentials')
        ? 'არასწორი მიმდინარე პაროლი'
        : 'პაროლის შეცვლისას მოხდა შეცდომა';

      toast.error(message);
      throw err;
    }
  }, [user, toast]);

  // პაროლის აღდგენა (Email გადაგზავნა)
  const resetPassword = useCallback(async (email) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.toLowerCase().trim(), {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      toast.success('პაროლის აღდგენის ინსტრუქცია გაიგზავნა თქვენს ელ-ფოსტაზე!');
      return true;
    } catch (err) {
      console.error('Password reset error:', err);
      captureError(err, { tags: { feature: 'auth', action: 'reset_password' } });
      toast.error('პაროლის აღდგენისას მოხდა შეცდომა');
      throw err;
    }
  }, [toast]);

  // პროფილის განახლება
  const updateProfile = useCallback(async (updates) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id)
        .select()
        .single();

      if (error) throw error;

      setProfile(data);

      // ლოგირება audit-ში
      await supabase.from('audit_logs').insert([{
        table_name: 'user_profiles',
        record_id: user.id,
        action: 'UPDATE',
        event_type: 'PROFILE_UPDATE',
        new_data: { updates, updated_at: new Date().toISOString() },
        changed_by: user.id
      }]);

      toast.success('პროფილი განახლდა!');
      return data;
    } catch (err) {
      console.error('Profile update error:', err);
      captureError(err, { tags: { feature: 'auth', action: 'profile_update' } });
      toast.error('პროფილის განახლებისას მოხდა შეცდომა');
      throw err;
    }
  }, [user, toast]);

  return {
    user,
    profile,
    isLoading,
    error,
    isAuthenticated: !!user,
    signIn,
    signOut,
    changePassword,
    resetPassword,
    updateProfile,
  };
}

/**
 * მომხმარებლის როლის შემოწმება
 */
export function useUserRole() {
  const { profile, isLoading } = useAuth();

  return {
    role: profile?.role || 'user',
    isAdmin: profile?.role === 'admin',
    isLoading,
  };
}
